// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { getMessages, modifyChat, searchMessages } from '../../services/api';
import ChatList from './components/ChatList';
import ChatWindow from './components/ChatWindow';
import Header from '../../components/header/header';
import { RoutesPaths } from '../../Router/constant';
import { useNavigate } from 'react-router';
import { isMobile } from 'react-device-detect';
import UserProfile from './userProfile';
import { useDispatch, useSelector } from 'react-redux';
import { chatLoadingAction, chatMediaPreviewerAction } from '../../actions/chat';
import { getPromoterAction } from '../../actions/promoters';
import { getOfficerList } from '../../actions/officer';
import { getShareHolderList } from '../../actions/shareHolder';
import { getAllChatList, updateChatLastMessage } from '../../actions/chatv2';
import { getProfileAction } from '../../actions/user';
import ImageViewer from 'react-simple-image-viewer';
import { resetAllSignatureRequestByChannelIdAction } from '../../actions/signnow';
import { getCapitalList } from '../../actions/capital';
import Loader from '../../components/loader';
import { getAllCompany } from '../../actions/company';
import { playChatNotificationSound, primeChatNotificationSound } from '../../utils/chatNotificationSound';

export const getName = (chat, user, type = false) => {
  const participants = chat?.participants || [];

  // 1) Explicit chat name wins
  if (chat?.name) {
    return chat.name;
  }

  // Helper to render a single participant’s name + icon
  const renderParticipant = (p) => {
    const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
    if (type) {
      return fullName
    } else {
      return (
        <React.Fragment key={p.id}>
          {fullName}
          {p.isEmailVerified === false && (
            <img
              src="/assets/img/unverified_user.svg"
              alt="Unverified"
              style={{
                width: 16,
                height: 16,
                marginLeft: 4,
                verticalAlign: 'middle',
              }}
            />
          )}
        </React.Fragment>
      );
    }
  };

  // 2) Group chat (>2 participants): render each name+icon, separated by commas
  if (participants.length > 2) {
    if (type) {
      return participants
        .map(p => `${p.first_name} ${p.last_name}`.trim())
        .join(', ');
    } else {
      const items = participants.map((p, i) => (
        <React.Fragment key={p.id}>
          {renderParticipant(p)}
          {i < participants.length - 1 && ', '}
        </React.Fragment>
      ));
      return <>{items}</>;
    }

  }

  // 3) One-to-one: find the “other” participant
  const other = participants.find(p => p.id !== user?.id) || {};
  return renderParticipant(other);
};

export default function MessageDetail() {
  const params = new URLSearchParams(window.location.search);
  const currentUserId = params.get('room');
  const ispublic = params.get('ispublic');
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selected, setSelected] = useState(null); // { chat, lastMessage }
  const [messages, setMessages] = useState([]);   // active chat messages
  const [unread, setUnread] = useState({});   // { [chatId]: count }
  const [socket, setSocket] = useState(null);
  const [searchResults, setSearchResults] = useState(null);// [{message, chat}]
  const [highlightMsgId, setHighlightMsgId] = useState(null);
  const [showProfile, setShowProfile] = useState(isMobile || ispublic ? false : true);
  const [showList, setShowList] = useState(isMobile ? true : false);
  const { user } = useSelector(state => state?.user);
  const { selectedCompany } = useSelector((state) => state.company);
  const { channelList } = useSelector(state => state.chatv2);
  const { currentImage, isViewerOpen, chatMediaFileName } = useSelector((state) => state.chat);
  const loadFirstRef = useRef(true);
  const { isChatLoading } = useSelector(state => state.chat)

  useEffect(() => primeChatNotificationSound(), []);
  const openProfile = () => {
    if (!ispublic) {
      setShowProfile(!showProfile);
    }
  };

  const selRef = useRef(selected);

  useEffect(() => {
    getProfileFunc()
  }, [])


  const getProfileFunc = async () => {
    dispatch(getAllCompany());
    var resp = await dispatch(getProfileAction())
    if (!resp?.status) {
      navigate(RoutesPaths.HOME);
    }
  }

  useEffect(() => { selRef.current = selected; }, [selected]);

  useEffect(() => {
    if (user?.id) {
      const s = io(process.env.REACT_APP_BACKEND_URL, {
        query: { userId: user && user?.id }
      });
      setSocket(s);
      s.on('newMessage', msg => {
        dispatch(updateChatLastMessage(msg.chat, msg));
        if (msg?.sender?.id !== user?.id) {
          playChatNotificationSound();
        }
        if (selRef.current?.chat._id === msg.chat) {
          setMessages(ms => [...ms, msg]);
          setUnread(u => ({ ...u, [msg.chat]: 0 }));
        } else {
          setUnread(u => ({
            ...u,
            [msg.chat]: (u[msg.chat] || 0) + 1
          }));
        }
      });
      loadChats(s);
      s.on('newChat', async (newChat) => {
        s.emit('joinChat', newChat);
        dispatch(getAllChatList()).then(chats => {
          const entry = chats.find(c => c.chat._id === newChat);
          const count = entry?.unreadCount ?? 0;

          // 3) seed just that chat’s badge with the real value
          setUnread(u => ({
            ...u,
            [newChat]: count
          }));
        });
      });
      return () => s.disconnect();
    }
  }, [user,]);

  useEffect(() => {
    const { id } = selectedCompany;
    if (id) {
      const fetchData = async () => {
        dispatch(getShareHolderList(id));
        dispatch(getOfficerList(id));
        dispatch(getPromoterAction(id));
        dispatch(getCapitalList(id));

      };
      fetchData().catch(console.error);
    }
    return () => { };
  }, [dispatch, selectedCompany]);


  useEffect(() => {
    if (!socket || !selected) return;
    socket.emit('joinChat', selected?.chat?._id);
    socket.emit('markRead', selected?.chat?._id);
    getMessages(selected?.chat?._id).then(setMessages);
    setUnread(u => ({ ...u, [selected?.chat?._id]: 0 }));
  }, [socket, selected]);

  const loadChats = async (socket, isLoader = false) => {
    loadFirstRef.current = false;
    if (!isLoader) {
      dispatch(chatLoadingAction(true))
    }
    const data = await dispatch(getAllChatList());
    dispatch(chatLoadingAction(false))
    const initialUnread = {};
    data.forEach(({ chat, unreadCount = 0 }) => {
      initialUnread[chat._id] = unreadCount;
      socket.emit('joinChat', chat?._id);
    });
    setUnread(initialUnread);
    if (data?.length > 0) {
      let match = null;
      const sortedChats = [...data].sort((a, b) => {
        const aTime = new Date(a.lastMessage?.updatedAt || 0);
        const bTime = new Date(b.lastMessage?.updatedAt || 0);
        return bTime - aTime;  // descending
      });
      if (currentUserId) {
        match = sortedChats?.find(c => c.chat._id === currentUserId);
      }
      openChat(match || sortedChats[0])
    }
  };

  const openChat = (entry, messageId = null) => {
    setMessages([])
    setSelected(entry);
    setHighlightMsgId(messageId);
    setShowList((loadFirstRef?.current && isMobile) ? true : false)
    dispatch(resetAllSignatureRequestByChannelIdAction())
    if (ispublic) {
      navigate(RoutesPaths.MESSAGE_DETAIL_PUBLIC + "?room=" + entry?.chat?._id + "&ispublic=true");
    } else {
      navigate(RoutesPaths.MESSAGE_DETAIL + "?room=" + entry?.chat?._id);
    }
  };

  const onSearch = async q => {
    const trimmed = q.trim();
    if (!trimmed) {
      setSearchResults(null); // clear if empty
      return;
    }
    const results = await searchMessages(trimmed);
    setSearchResults(results);
  };

  const renameChat = async newName => {
    if (!selected) return;
    if (newName?.trim()) {
      const updated = await modifyChat(currentUserId, { name: newName })
      dispatch(updateChatLastMessage(updated._id, selected.lastMessage));
      await dispatch(getProfileAction());
      setSelected(updated);
    }
  };

  const sendMessage = (content, filesResp) => {
    if (socket && selected) {
      if (filesResp && filesResp.length > 0) {
        socket.emit('sendMessage', {
          chatId: selected.chat._id,
          content: content,
          module: "FILE",
          data: filesResp
        });
      } else {
        socket.emit('sendMessage', {
          chatId: selected.chat._id,
          content
        });
      }

    }
  };

  const closeImageViewer = () => {
    dispatch(chatMediaPreviewerAction(0, false));
  };
  if (isChatLoading) {
    return (
      <>
        <Loader />
      </>
    )

  } else {
    if (channelList?.length === 0) {
      return (
        <>
          <Header changePosition={true} title="" subtitle="" />
          <div className="container-fluid message-page">
            <div className="row messageRow">
              <div className="col-12">
                <div className="empty">No chat Found</div>
              </div>
            </div>
          </div>
        </>
      )
    }
  }


  return (
    <>
      {!ispublic &&
        <Header changePosition={true} title="" subtitle="" />
      }
      <div className={`container-fluid message-page ${ispublic ? "ispublic-message-page" : ""}`}>
        {isMobile && (
          <div
            className={`userlistToggle ${showList ? 'hasShowList' : 'noShowList'}`}
            onClick={() => setShowList(!showList)}
          >
            {showList ? (
              <i className="fa-solid fa-xmark"></i>
            ) : (
              <>
                <i className="sidenav-toggler-line"></i>
                <i className="sidenav-toggler-line"></i>
                <i className="sidenav-toggler-line"></i>
              </>
            )}
          </div>
        )}
        <div className="row messageRow">
          <div
            className={
              showList
                ? "col-md-4 col-lg-3 messageLeft customScroll showUserlist"
                : "col-md-4 col-lg-3 messageLeft customScroll"
            }
          >
            <ChatList
              chats={channelList}
              unreadCounts={unread}
              searchResults={searchResults}
              onSelect={openChat}
              onSearch={onSearch}
              selected={selected}
            />
          </div>
          <div
            className={
              showProfile
                ? "col-md-5 col-lg-6 messageRight collapsediv"
                : "col-md-8 col-lg-9 messageRight"
            }
          >
            <ChatWindow
              chat={selected?.chat}
              messages={messages}
              openProfile={openProfile}
              highlightMessageId={highlightMsgId}
              currentUserId={currentUserId}
              onSend={sendMessage}
              showProfile={showProfile}
              searchQuery={searchResults}
              ispublic={ispublic}
            />
          </div>
          {!ispublic &&
            <div
              className={
                showProfile
                  ? "col-md-3 col-lg-3 userProfile customScroll showProfile"
                  : "col-md-3 userProfile customScroll"
              }
            >
              <UserProfile
                conversation={selected?.chat}
                setShowProfile={setShowProfile}
                participants={selected?.chat?.participants}
                currentUserId={currentUserId}
                onRename={renameChat}
                channelList={channelList}
                setSelected={setSelected}
              />
            </div>
          }
        </div>
      </div>
      {isViewerOpen && (
        <ImageViewer
          src={chatMediaFileName}
          currentIndex={currentImage}
          disableScroll={false}
          closeOnClickOutside={true}
          onClose={closeImageViewer}
          backgroundStyle={{ background: '#52594d', zIndex: 9999999999999 }}
        />
      )}
    </>
  );
}
